





















































































import Vue from 'vue';
import Component, { mixins } from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import canAutoplay, { CheckResponse } from 'can-autoplay';
import ResponsiveImage from '@/common/vue/ResponsiveImage.vue';
import MainNavigation from '@/webcomponents/header/MainNavigation.vue';
import ResizeUpdateMixin from '@/common/ts/resizing/ResizeUpdateMixin';
import MainNavigationItem from '@/webcomponents/header/MainNavigationItem.vue';
import MetaNavigation from '@/webcomponents/header/components/MetaNavigation.vue';
import type HeaderMobileMenu from '@/webcomponents/header/components/HeaderMobileMenu.vue';
import type HeaderFlyout from '@/webcomponents/header/components/HeaderFlyout.vue';
import type Search from '@/webcomponents/header/components/Search.vue';
import TrackingPushService from '@/common/ts/tracking/TrackingPushService';
import { JumpToEventBusEvents, JumpToEventBus } from '@/webcomponents/header/components/JumpToEventBus';
import VideoPauseHandler from '@/common/ts/pausing-videos/VideoPauseHandler';
import { SearchEventBus, SearchEventBusEvents } from '@/webcomponents/header/components/SearchEventBus';

const HeaderMobileMenuDynamic = () => import('@/webcomponents/header/components/HeaderMobileMenu.vue');
const HeaderFlyoutDynamic = () => import('@/webcomponents/header/components/HeaderFlyout.vue');
const SearchDynamic = () => import('@/webcomponents/header/components/Search.vue');
const LoginFlyout = () => import('@/webcomponents/header/components/LoginFlyout.vue');

export const HEADER_HEIGHT = 288;
export const SCROLLED_OFFSET = 25;
export const SCROLLED_BACK_ENABLED_OFFSET = 25;
export const MIN_HEIGHT_LARGE_HEADER_LANDSCAPE = 840;
export const MIN_HEIGHT_LARGE_HEADER_PORTRAIT = 660;

const SCROLL_STATE_LARGE = 'large';
const SCROLL_STATE_SCROLLING = 'scrolling';
const SCROLL_STATE_SNAP = 'snap';
const SCROLL_STATE_SNAP_BACK = 'snap-back';
const SCROLL_STATE_DOWN = 'scroll-down';
const SCROLL_STATE_UP = 'scroll-up';
const SCROLL_STATE_FLYOUT_OPEN = 'flyout-open';
const SCROLL_STATE_FLYOUT_CLOSE = 'flyout-close';
const SCROLL_STATE_MENU_OPEN = 'menu-open';
const SCROLL_STATE_MENU_CLOSE = 'menu-close';
const SCROLL_STATE_SEARCH_OPEN = 'search-open';
const SCROLL_STATE_SEARCH_CLOSE = 'search-close';
const SCROLL_STATE_SWITCH_TO_SEARCH = 'switch-to-search'; // switch from flyout to search
const SCROLL_STATE_SWITCH_TO_MENU = 'switch-to-menu'; // switch from search to menu (only possible on mobile)

// The order [ with large header ] scrolling down: [ large > scrolling > ] scroll-down
// The order [ with large header ] scrolling up: scroll-up [ snap-back > scrolling > large ]
type ScrollStateTypes = 'large'|'scrolling'|'snap'|'snap-back'|'scroll-down'|'scroll-up'|'flyout-open'|'menu-open'|'menu-close'
                        |'search-open'|'search-close'|'switch-to-search'|'switch-to-flyout';

const HeaderProps = Vue.extend({
  props: {
    logoJson: String,
    logoLink: String,
    logoDescription: String,
    logoVideo: String,
    showLarge: Boolean,
    privacyActionName: String,
    searchApiEndpoint: String,
  },
});

@Component({
  components: {
    HeaderFlyoutDynamic,
    HeaderMobileMenuDynamic,
    MainNavigation,
    MetaNavigation,
    ResponsiveImage,
    SearchDynamic,
    LoginFlyout,
  },
})
export default class Header extends mixins(HeaderProps, ResizeUpdateMixin) {
  $refs!: {
    headerFlyout: HeaderFlyout;
    mobileMenu: HeaderMobileMenu;
    search: Search;
    metaNavigation: MetaNavigation;
    videoPlayer: HTMLVideoElement;
    headerLink: HTMLElement;
    loginFlyout: HTMLElement;
  }

  scrollPos = window.scrollY;
  scrollProgress = 0;
  activeMainNavItem: MainNavigationItem | null = null;
  showVideo = false;
  canShowVideo = false;
  showLogo = true;
  scrolledBackTimer: number | null = null;
  scrollState: ScrollStateTypes | string = SCROLL_STATE_DOWN;
  nextScrollState: ScrollStateTypes | string = '';
  oldScrollState = '';
  mobileOldScrollState = '';
  snapDuration = 500;
  snapBackDuration = 500;
  isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
  showLoginFlyout = false;
  loginRecentlyOpened = false;

  @Watch('activeMainNavItem')
  handleMainNavSwitch (newItem: MainNavigationItem | null, oldItem: MainNavigationItem | null): void {
    if (this.showVideo) {
      this.hideVideo();
    }

    if (oldItem) {
      oldItem.isActive = false;
    }
    if (newItem) {
      newItem.isActive = true;
    } else if (this.scrollState === SCROLL_STATE_FLYOUT_OPEN) {
      this.scrollState = this.oldScrollState;
    }
  }

  onMobileMenuClick (): void {
    if (SCROLL_STATE_MENU_OPEN === this.scrollState) {
      this.closeMenu();
    } else if (SCROLL_STATE_SEARCH_OPEN === this.scrollState) {
      this.scrollState = SCROLL_STATE_SWITCH_TO_MENU;
      this.closeMenu();
    } else {
      VideoPauseHandler.getInstance().pauseAllVideos();
      if (!this.isSwitching) {
        this.mobileOldScrollState = this.scrollState;
        this.hideVideo();
      }
      this.scrollState = SCROLL_STATE_MENU_OPEN;
      // body scroll lock handled by mobile menu
    }
  }

  // any is not nice here but there is no really good typification for CustomEvents in Typescript
  onMainNavClick (evt: any) {
    if (evt?.detail?.clickedItem && this.scrollState !== SCROLL_STATE_MENU_OPEN) {
      VideoPauseHandler.getInstance().pauseAllVideos();
      const mainNavItem: MainNavigationItem = evt.detail.clickedItem;
      if (this.activeMainNavItem === mainNavItem) {
        this.$refs.headerFlyout.closeSubMenu();
      } else if (!this.activeMainNavItem) {
        disableBodyScroll(this.$el);
        this.oldScrollState = this.scrollState;
        if ([SCROLL_STATE_LARGE, SCROLL_STATE_SCROLLING].includes(this.scrollState)) {
          this.scrollState = SCROLL_STATE_SNAP;
          setTimeout(() => {
            this.scrollState = SCROLL_STATE_FLYOUT_OPEN;
            this.activeMainNavItem = mainNavItem;
          }, this.snapDuration);
        } else {
          this.scrollState = SCROLL_STATE_FLYOUT_OPEN;
          this.activeMainNavItem = mainNavItem;
        }
      } else {
        this.activeMainNavItem = mainNavItem;
      }
    }
  }

  onSearchClick () {
    if (this.scrollState === SCROLL_STATE_SEARCH_OPEN) {
      this.closeMenu();
      VideoPauseHandler.getInstance().playPausedVideos();
    } else if ([SCROLL_STATE_FLYOUT_OPEN, SCROLL_STATE_MENU_OPEN].includes(this.scrollState)) {
      this.scrollState = SCROLL_STATE_SWITCH_TO_SEARCH;
      this.closeMenu();
    } else {
      if (!this.isSwitching) {
        // body scroll lock handled by search
        this.oldScrollState = this.scrollState;
      }
      VideoPauseHandler.getInstance().pauseAllVideos();
      this.scrollState = SCROLL_STATE_SEARCH_OPEN;
    }
  }

  onLoginClick () {
    if (this.showLoginFlyout) {
      this.showLoginFlyout = false;
    } else {
      this.showLoginFlyout = true;
      this.loginRecentlyOpened = true;
      setTimeout(() => {
        this.loginRecentlyOpened = false;
      }, 500);
    }
  }

  onLoginClose () {
    if (!this.loginRecentlyOpened) {
      this.showLoginFlyout = false;
      this.$refs.metaNavigation.loginOpen = false;
    }
  }

  closeMenu () {
    if (this.$refs.headerFlyout) {
      this.$refs.headerFlyout.closeOverlay();
    }
    if (this.$refs.mobileMenu) {
      this.$refs.mobileMenu.closeOverlay();
    }
    if (this.$refs.search) {
      this.$refs.search.closeOverlay();
    }
    enableBodyScroll(this.$el);
  }

  // It has become a bit complex to get to the correct state after the menu has been closed.
  finalizeFlyoutClose () {
    if (this.scrollState === SCROLL_STATE_SWITCH_TO_SEARCH) {
      this.onSearchClick();
    } else if (this.scrollState === SCROLL_STATE_SWITCH_TO_MENU) {
      VideoPauseHandler.getInstance().playPausedVideos();
      this.onMobileMenuClick();
    } else if (this.scrollState === SCROLL_STATE_MENU_OPEN) {
      VideoPauseHandler.getInstance().playPausedVideos();
      this.scrollState = SCROLL_STATE_MENU_CLOSE;
      enableBodyScroll(this.$el);
      setTimeout(() => {
        this.scrollState = this.mobileOldScrollState;
        this.mobileOldScrollState = '';
      }, 400);
    } else if (this.scrollState === SCROLL_STATE_SEARCH_OPEN) {
      VideoPauseHandler.getInstance().playPausedVideos();
      this.scrollState = SCROLL_STATE_SEARCH_CLOSE;
      // body scroll lock handled by search
      setTimeout(() => {
        this.scrollState = this.oldScrollState;
      }, 400);
    } else {
      VideoPauseHandler.getInstance().playPausedVideos();
      this.scrollState = SCROLL_STATE_FLYOUT_CLOSE;
      enableBodyScroll(this.$el);
      setTimeout(() => {
        this.activeMainNavItem = null;
        this.scrollState = this.oldScrollState;
      }, 400);
    }
  }

  focusLogo () {
    this.$refs.headerLink.focus();
  }

  logoTab (event: KeyboardEvent) {
    if (this.searchOpen) {
      event.preventDefault();
      SearchEventBus.$emit(SearchEventBusEvents.FocusInput);
    }
  }

  logoShiftTab (event: KeyboardEvent) {
    if (this.searchOpen) {
      if (this.isDesktop) {
        event.preventDefault();
        SearchEventBus.$emit(SearchEventBusEvents.FocusCloseButton);
      }
    }
  }

  onKeyUp (keyUpEvent: KeyboardEvent) {
    if (!keyUpEvent.isComposing && keyUpEvent.key === 'Escape') {
      if (this.activeMainNavItem) {
        this.closeMenu();
      }
      if (this.searchOpen) {
        this.$refs.search.closeOverlay();
      }
    }
  }

  async canAutoplay (): Promise<CheckResponse> {
    return Promise.resolve()
      .then(() => new Promise((resolve) => setTimeout(() => resolve(), 500)))
      .then(() => canAutoplay.video({ inline: true, muted: true }));
  }

  // Checks if video can be played (eg. iPhone low power mode)
  async handleBlockingBrowsers () {
    this.canAutoplay().then(({ error }) => {
      if (error && error.name === 'NotAllowedError') {
        this.hideVideo();
      } else {
        this.canShowVideo = true;
      }
    });
  }

  hideVideo () {
    if (this.showVideo && !this.showLogo) {
      this.showLogo = true;
      setTimeout(() => {
        this.showVideo = false;
      }, 350);
    }
  }

  onScroll () {
    const oldScrollPos = this.scrollPos;
    const newScrollPos = window.scrollY;

    // prevent menu closing by auto scrolling to form on iOS
    if (this.scrollState === SCROLL_STATE_SEARCH_OPEN) {
      return;
    }
    if (this.isLargeHeader) {
      // When initially scrolling down
      if (this.scrollState === SCROLL_STATE_LARGE && newScrollPos > 0) {
        this.scrollState = SCROLL_STATE_SCROLLING;
      }

      // Execute snap transition
      if (this.scrollState === SCROLL_STATE_SCROLLING && newScrollPos > SCROLLED_OFFSET) {
        this.scrollState = SCROLL_STATE_SNAP;
        this.nextScrollState = SCROLL_STATE_DOWN;
        setTimeout(() => {
          this.scrollState = this.nextScrollState;
          this.nextScrollState = '';
        }, this.snapDuration);
        this.hideVideo();
      }

      if (this.scrollState === SCROLL_STATE_SCROLLING && newScrollPos < oldScrollPos && newScrollPos < 10) {
        this.scrollState = SCROLL_STATE_LARGE;
      } else if ([SCROLL_STATE_DOWN, SCROLL_STATE_UP].includes(this.scrollState) && newScrollPos < SCROLLED_OFFSET) {
        this.calculateProgress();
        this.scrollState = SCROLL_STATE_SNAP_BACK;
        setTimeout(() => {
          this.scrollState = SCROLL_STATE_LARGE;
          const currentScrollPos = window.scrollY;
          if (currentScrollPos > SCROLLED_OFFSET) {
            this.scrollState = SCROLL_STATE_DOWN;
          }
        }, this.snapBackDuration);
      } else if (this.scrollState === SCROLL_STATE_SNAP && newScrollPos < SCROLLED_OFFSET) {
        this.nextScrollState = SCROLL_STATE_SNAP_BACK;
        setTimeout(() => {
          this.scrollState = SCROLL_STATE_LARGE;
          const currentScrollPos = window.scrollY;
          if (currentScrollPos > SCROLLED_OFFSET) {
            this.scrollState = SCROLL_STATE_DOWN;
          }
        }, this.snapBackDuration);
      }
    } else if (this.scrollState !== SCROLL_STATE_DOWN && newScrollPos > SCROLLED_OFFSET && oldScrollPos <= SCROLLED_OFFSET) {
      this.scrollState = SCROLL_STATE_DOWN;
    } else if (this.scrollState === SCROLL_STATE_DOWN && newScrollPos < SCROLLED_OFFSET) {
      this.scrollState = SCROLL_STATE_UP;

      if (this.scrolledBackTimer) {
        clearTimeout(this.scrolledBackTimer);
        this.scrolledBackTimer = null;
      }
    }

    if (this.scrollState === SCROLL_STATE_UP && newScrollPos > oldScrollPos) {
      // scrolled forward
      this.scrollState = SCROLL_STATE_DOWN;
      if (this.scrolledBackTimer) {
        clearTimeout(this.scrolledBackTimer);
        this.scrolledBackTimer = null;
      }
    } else if (this.scrollState === SCROLL_STATE_DOWN && newScrollPos < oldScrollPos && newScrollPos > SCROLLED_BACK_ENABLED_OFFSET) {
      // scrolled back
      if (!this.scrolledBackTimer) {
        this.scrolledBackTimer = setTimeout(() => {
          this.scrollState = SCROLL_STATE_UP;
          this.scrolledBackTimer = null;
        }, 100);
      }
    }

    if (this.isLargeHeader && this.scrollState === SCROLL_STATE_SCROLLING) {
      this.calculateProgress();
    }

    // remember current scroll pos
    this.scrollPos = newScrollPos;
  }

  // It's more reliable to have a function that is called then to trust on vue's recalculation in computed properties
  calculateProgress (): void {
    this.scrollProgress = Math.max(1 - (this.scrollPos / HEADER_HEIGHT), 0);
  }

  @Watch('viewportSize')
  onViewportChange (newValue: string, oldValue: string) {
    const mobileViewports = ['tablet', 'tabletPortrait', 'mobile'];
    if ((oldValue === 'desktop' && mobileViewports.includes(newValue) && this.scrollState === SCROLL_STATE_FLYOUT_OPEN)
      || (mobileViewports.includes(oldValue) && newValue === 'desktop' && this.scrollState === SCROLL_STATE_MENU_OPEN)) {
      this.finalizeFlyoutClose();
    }
  }

  @Watch('scrollState')
  dispatchEvent (): void {
    const event = new CustomEvent('header-state-change', {
      bubbles: true,
      composed: true,
      detail: {
        scrollState: this.scrollState,
        scrollProgress: this.scrollProgress,
      },
    });
    this.$el.dispatchEvent(event);
  }

  get rootClass () {
    return {
      'brandhub-header--large': this.scrollState === SCROLL_STATE_LARGE,
      'brandhub-header--menu-open': this.scrollState === SCROLL_STATE_MENU_OPEN,
      'brandhub-header--flyout-open': this.scrollState === SCROLL_STATE_FLYOUT_OPEN,
      'brandhub-header--flyout-close': this.scrollState === SCROLL_STATE_FLYOUT_CLOSE,
      'brandhub-header--search-open': this.scrollState === SCROLL_STATE_SEARCH_OPEN,
      'brandhub-header--search-close': this.scrollState === SCROLL_STATE_SEARCH_CLOSE,
      'brandhub-header--switches-from-or-to-search': this.isSwitching,
      'brandhub-header--scroll-down': this.scrollState === SCROLL_STATE_DOWN,
      'brandhub-header--scroll-up': this.scrollState === SCROLL_STATE_UP,
      'brandhub-header--scrolling': this.scrollState === SCROLL_STATE_SCROLLING,
      'brandhub-header--snap': this.scrollState === SCROLL_STATE_SNAP,
      'brandhub-header--snap-back': this.scrollState === SCROLL_STATE_SNAP_BACK,
      'brandhub-header--show-logo': this.showLogo,
      'brandhub-header--has-video': !!this.logoVideo,
      'brandhub-header--firefox': this.isFirefox,
    };
  }

  get scrollStateAwareProgress () {
    let scrollProgress = 0;

    if ([SCROLL_STATE_LARGE, SCROLL_STATE_SNAP_BACK].includes(this.scrollState)) {
      scrollProgress = 1;
    } else if (this.scrollState === SCROLL_STATE_SCROLLING) {
      scrollProgress = this.scrollProgress;
    }

    return scrollProgress;
  }

  get rootStyle () {
    return {
      '--scroll-progress': this.scrollStateAwareProgress,
      '--fade-in-delay': this.logoVideo && this.showVideo && !this.searchOpen
        ? '1500ms'
        : '0ms',
      '--snap-back-duration': `${this.snapBackDuration}ms`,
    };
  }

  get mobileMenuOpen (): boolean {
    return !this.isDesktop && [SCROLL_STATE_MENU_OPEN, SCROLL_STATE_SWITCH_TO_SEARCH].includes(this.scrollState);
  }

  get searchOpen (): boolean {
    return [SCROLL_STATE_SEARCH_OPEN, SCROLL_STATE_SEARCH_CLOSE, SCROLL_STATE_SWITCH_TO_MENU].includes(this.scrollState);
  }

  /**
   * Checks if it's currently switching from or to search
   */
  get isSwitching (): boolean {
    return [SCROLL_STATE_SWITCH_TO_SEARCH, SCROLL_STATE_SWITCH_TO_MENU].includes(this.scrollState);
  }

  get isLargeHeader (): boolean {
    return this.showLarge
      && ((this.windowWidth >= this.windowHeight && this.windowHeight >= MIN_HEIGHT_LARGE_HEADER_LANDSCAPE)
      || (this.windowWidth < this.windowHeight && this.windowHeight >= MIN_HEIGHT_LARGE_HEADER_PORTRAIT));
  }

  onLogoClick (): void {
    if (this.logoLink) {
      TrackingPushService.getInstance().pushTrackingAttributes('link', 'Logo', 'Mercedes-Benz logo', this.logoLink);
    }
  }

  scrollToHeader () {
    this.$el.scrollIntoView({ behavior: 'smooth' });
  }

  created (): void {
    // Checks if video can be played (eg. iPhone low power mode prevents that)
    this.handleBlockingBrowsers();
    this.windowHeight = window.innerHeight; // windowHeight of resize mixin not yet initialized
    this.windowWidth = window.innerWidth; // windowHeight of resize mixin not yet initialized
    if (this.isLargeHeader && this.logoVideo) {
      this.scrollState = 'large';
      // If the user has already / previously scrolled on the page we don't show the video
      if (window.scrollY > 10) {
        this.scrollState = 'scroll-up';
      } else {
        this.showVideo = true;
        this.showLogo = false;
      }
    }
    JumpToEventBus.$on(JumpToEventBusEvents.JumpToNavigation, this.scrollToHeader);
  }

  mounted () {
    this.onScroll();
    document.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('keyup', this.onKeyUp, { passive: true });
    this.$el.addEventListener('mainNavClicked', this.onMainNavClick);
    this.$el.addEventListener('loginClicked', this.onLoginClick);
    window.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.onLoginClose();
      }
    }, { passive: true });
  }
}
